import { useCallback, useEffect, useState } from "react";

import { ApiError, invitations } from "./api";

/**
 * Invitations.
 *
 * L'inscription est fermée : un compte ne se crée qu'avec un code remis par
 * quelqu'un. Ces codes s'émettaient jusqu'ici en appelant l'API à la main, ce
 * qui rendait la fermeture impraticable — d'où cet écran.
 *
 * Réservé aux administrateurs, comme la route.
 */

type Invitation = {
  code: string;
  label: string | null;
  expiresAt: string;
  state: "open" | "used" | "expired";
  usedBy: string | null;
};

const STATES: Record<Invitation["state"], string> = {
  open: "Disponible",
  used: "Utilisée",
  expired: "Expirée",
};

export function Invitations() {
  const [list, setList] = useState<Invitation[] | null>(null);
  const [label, setLabel] = useState("");
  const [days, setDays] = useState(14);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const reload = useCallback(async () => {
    try {
      setList(await invitations.list());
    } catch (cause) {
      // Un organisateur n'est pas administrateur : l'écran se retire plutôt que
      // d'afficher une erreur qui ne le concerne pas.
      if (cause instanceof ApiError && cause.status === 403) setForbidden(true);
      else setMessage("Lecture impossible.");
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (forbidden) return null;

  const create = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const created = await invitations.create(label.trim(), days);
      setLabel("");
      setMessage(`Code créé : ${created.code}`);
      await reload();
    } catch {
      setMessage("Création impossible.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h2>Invitations</h2>
      <p className="muted">
        L'inscription est fermée : un compte ne se crée qu'avec l'un de ces codes. Il
        sert une fois, puis expire.
      </p>

      <div className="row">
        <label style={{ flex: 1, minWidth: 220 }}>
          Pour qui ?
          <input
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Marie, testeur du festival X…"
          />
        </label>
        <label>
          Valable (jours)
          <input
            type="number"
            min={1}
            max={365}
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            style={{ width: 100 }}
          />
        </label>
        <button className="primary" onClick={() => void create()} disabled={busy}>
          Créer un code
        </button>
      </div>

      {message ? <p className="muted">{message}</p> : null}

      {list && list.length > 0 ? (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Pour qui</th>
                <th>État</th>
                <th>Expire</th>
              </tr>
            </thead>
            <tbody>
              {list.map((invitation) => (
                <tr key={invitation.code}>
                  {/* Never split on its hyphen: the code is read out loud. */}
                  <td className="nowrap">
                    <code>{invitation.code}</code>
                  </td>
                  <td>{invitation.label ?? "—"}</td>
                  <td>
                    <span
                      className={
                        invitation.state === "open" ? "badge published" : "badge"
                      }
                    >
                      {STATES[invitation.state]}
                      {invitation.usedBy ? ` · ${invitation.usedBy}` : ""}
                    </span>
                  </td>
                  <td className="muted">
                    {new Date(invitation.expiresAt).toLocaleDateString("fr-FR")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted">Aucune invitation émise.</p>
      )}
    </section>
  );
}
